import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalStorage } from 'react-use';
import { useLocation } from 'react-router-dom';

import { logInApple, getAuthFlowFromPath, SIGNUP_PATH } from 'utils/auth';
import { AppleLogo } from 'static';
import { Loader } from 'components';

import { useResetOAuthButtonLoadingOnReturn } from '../useResetOAuthButtonLoadingOnReturn';
import { Button } from './styled';

interface AppleAuthButtonProps {
  disabled?: boolean;
  forceLoading?: boolean;
  onAuthStart?: () => void;
  highlighted?: boolean;
  style?: { [key: string]: string };
  iconOnly?: boolean;
}

const AppleAuthButton = ({
  style,
  disabled,
  forceLoading,
  onAuthStart,
  highlighted,
  iconOnly,
}: AppleAuthButtonProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const isMountedRef = useRef(true);
  const [prevAuth] = useLocalStorage<{ [key: string]: string }>(
    'socialSignIns',
    {},
  );
  const buttonText = useMemo(() => {
    let text = `${t('auth.signInWith')} Apple`;

    if (location.pathname !== SIGNUP_PATH && prevAuth?.apple) {
      text = `${t('auth.continueAs')} ${prevAuth.apple}`;
    }

    return text;
  }, [prevAuth, location, t]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useResetOAuthButtonLoadingOnReturn(setIsLoading, isMountedRef);

  const handleClick = () => {
    if (isLoading || forceLoading) return;
    onAuthStart?.();
    setIsLoading(true);
    logInApple({
      flow: getAuthFlowFromPath(location.pathname),
    }).catch(() => {
      if (!isMountedRef.current) return;
      setIsLoading(false);
    });
  };

  const effectiveLoading = Boolean(forceLoading || isLoading);

  return (
    <Button
      style={style}
      type="button"
      disabled={disabled || effectiveLoading}
      onClick={handleClick}
      $highlighted={highlighted && !disabled && !effectiveLoading}
      $iconOnly={iconOnly}
    >
      {effectiveLoading ? (
        <Loader type="button" size="20px" thickness={2} />
      ) : (
        <>
          <AppleLogo />
          {!iconOnly && buttonText}
        </>
      )}
    </Button>
  );
};

export default AppleAuthButton;
